import { FormEvent, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, ApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import type { Placement } from "@/types";

interface MyPlacement {
  id: string;
  platform: "myhome" | "ss";
  title: string | null;
  published_url: string;
  external_id: string | null;
  published_at: string;
}

type Phase = "form" | "sending" | "done";

/** Topbar "Support" icon button. Opens a small modal where the employee
 *  can flag problems with their recently uploaded placements (multi-select)
 *  and/or write a free-text note. The submission is forwarded to the
 *  configured Telegram chat. Lives in the same right-cluster as the
 *  notifications bell and chat trigger. */
export function SupportButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<MyPlacement[] | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [phase, setPhase] = useState<Phase>("form");
  const [err, setErr] = useState<string | null>(null);

  // Fetch the viewer's own most-recent placements when the modal opens.
  // We re-fetch every open so a freshly-published listing shows up
  // without a hard refresh.
  useEffect(() => {
    if (!open) return;
    if (!user) return;
    let cancelled = false;
    setLoadingList(true);
    setErr(null);
    (async () => {
      try {
        // Scope to the viewer's own placements — the support endpoint
        // only accepts IDs the sender uploaded, so showing teammates'
        // rows here would just produce confusing 404s on submit.
        const r = await api<{
          items: Placement[];
        }>(`/api/placements?limit=20&user_id=${encodeURIComponent(user.id)}`);
        if (cancelled) return;
        setItems(
          r.items.map((p) => ({
            id: p.id,
            platform: p.platform,
            title: p.title,
            published_url: p.published_url,
            external_id: p.external_id,
            published_at: p.published_at,
          })),
        );
      } catch (e) {
        if (cancelled) return;
        // Non-fatal — the user can still send a plain note.
        setItems([]);
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Body scroll-lock + Esc-to-close while the modal is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  function close() {
    setOpen(false);
    // Reset after the modal is closed so a re-open feels fresh, but only
    // when we're not mid-send.
    setTimeout(() => {
      setPhase("form");
      setNote("");
      setSelected(new Set());
      setErr(null);
    }, 200);
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!note.trim()) {
      setErr("Please describe the problem before sending.");
      return;
    }
    setErr(null);
    setPhase("sending");
    try {
      await api("/api/support", {
        method: "POST",
        json: {
          note: note.trim(),
          placement_ids: Array.from(selected),
        },
      });
      setPhase("done");
      setTimeout(close, 1600);
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Could not send. Try again in a moment.",
      );
      setPhase("form");
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Report a problem"
        aria-label="Report a problem"
        // Shares .icon-btn with NotificationsBell / ChatTrigger so the
        // right-hand cluster reads as one row.
        className="icon-btn"
      >
        <SupportIcon />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && phase !== "sending") close();
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="card w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl">
            <div className="p-4 border-b border-white/5 flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-accent-500/15 text-accent-300 flex items-center justify-center ring-1 ring-accent-500/30 flex-shrink-0">
                <SupportIcon />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-100">
                  Report a problem
                </div>
                <div className="text-[12px] text-slate-500">
                  Pick any recent placements that are broken, or just leave a note.
                </div>
              </div>
              {phase !== "sending" && (
                <button
                  type="button"
                  onClick={close}
                  className="text-slate-500 hover:text-slate-300 p-1"
                  aria-label="Close"
                >
                  ✕
                </button>
              )}
            </div>

            {phase !== "done" ? (
              <form
                onSubmit={submit}
                className="flex flex-col min-h-0 flex-1"
              >
                <div className="p-4 space-y-3 overflow-y-auto">
                  <div>
                    <div className="label">Recent placements</div>
                    {loadingList ? (
                      <div className="text-[12px] text-slate-500 py-2">
                        Loading…
                      </div>
                    ) : !items || items.length === 0 ? (
                      <div className="text-[12px] text-slate-500 py-2">
                        No recent placements found.
                      </div>
                    ) : (
                      <div className="rounded-lg border border-white/5 max-h-56 overflow-y-auto divide-y divide-white/5">
                        {items.map((p) => {
                          const checked = selected.has(p.id);
                          return (
                            <label
                              key={p.id}
                              className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer text-sm hover:bg-white/[0.03] ${
                                checked ? "bg-accent-500/[0.06]" : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggle(p.id)}
                                className="mt-1 flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-slate-200 truncate">
                                  {p.title || "(untitled)"}
                                </div>
                                <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                                  <span className="uppercase tracking-wider">
                                    {p.platform}
                                  </span>
                                  {p.external_id && (
                                    <span className="font-mono">
                                      · #{p.external_id}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {selected.size > 0 && (
                      <div className="mt-1.5 text-[11px] text-slate-500">
                        {selected.size} selected
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="label">Note</div>
                    <textarea
                      rows={5}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      maxLength={4000}
                      placeholder="Describe the problem — e.g. price wrong, photos missing, listing rejected on portal."
                      className="input w-full leading-relaxed"
                    />
                    <div className="mt-1 flex justify-end text-[11px] text-slate-500 tabular-nums">
                      {note.length}/4000
                    </div>
                  </div>

                  {err && (
                    <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
                      {err}
                    </div>
                  )}
                </div>

                <div className="p-3 border-t border-white/5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={close}
                    disabled={phase === "sending"}
                    className="text-sm px-4 py-2 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-slate-200 border border-white/10 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={phase === "sending" || !note.trim()}
                    className="text-sm px-4 py-2 rounded-md font-medium bg-accent-500/90 hover:bg-accent-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {phase === "sending" ? "Sending…" : "Send"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-500/15 text-emerald-300 flex items-center justify-center mx-auto mb-3 ring-1 ring-emerald-500/30">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="text-base font-semibold text-emerald-100">
                  Sent
                </div>
                <div className="text-sm text-slate-400 mt-1">
                  Thanks — we'll take a look.
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function SupportIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18.364 18.364A9 9 0 1 0 5.636 5.636 9 9 0 0 0 18.364 18.364z" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}
